import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, ROLE_META } from "@/context/AuthContext";
import { Card, SectionTitle, Pill, Loader } from "@/components/common";
import { Settings as SettingsIcon, Save, CalendarClock, Lock, Mail, Send, Users } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEPT_LABEL = { design: "Design", material: "Material", procurement: "Procurement", production: "Production", projects: "Projects", commercial: "Commercial" };

export default function Settings() {
  const { canEditSettings } = useAuth();
  const canEdit = canEditSettings;
  const [data, setData] = useState(null);
  const [weekend, setWeekend] = useState([]);
  const [lead, setLead] = useState({});
  const [sender, setSender] = useState("");
  const [recipients, setRecipients] = useState({});
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/settings");
    setData(data);
    setWeekend(data.weekend_days);
    setLead(data.lead_times);
    setSender(data.email_sender || "");
    setRecipients(data.dept_recipients || {});
    if (data.can_edit_settings) {
      try { const u = await api.get("/auth/users"); setUsers(u.data); } catch { /* non-admin */ }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader label="Loading settings" />;

  const toggleDay = (d) => {
    if (!canEdit) return;
    setWeekend((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d]));
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.put("/settings", { weekend_days: weekend, lead_times: lead, email_sender: sender, dept_recipients: recipients });
      setMsg("Settings saved. New projects, reschedules and emails use these values.");
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const runScan = async () => {
    setScanning(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/emails/run-now");
      setMsg(data.message || "Reminder scan complete.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setScanning(false); }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">Stage lead times & working-week configuration used by backward scheduling.</p>
        </div>
        {canEdit && (
          <button onClick={save} disabled={busy} data-testid="save-settings-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-60">
            <Save size={15} /> {busy ? "Saving…" : "Save settings"}
          </button>
        )}
      </div>

      {!canEdit && (
        <Card className="p-3.5 flex items-center gap-2 text-sm text-muted-foreground">
          <Lock size={15} className="text-gold" /> Only the Administrator can edit settings. Read-only view.
        </Card>
      )}
      {msg && <div className="text-sm text-success bg-success/10 border border-success/30 rounded-md px-3 py-2" data-testid="settings-msg">{msg}</div>}
      {err && <div className="text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2">{err}</div>}

      <section>
        <SectionTitle icon={CalendarClock}>Weekend days</SectionTitle>
        <Card className="p-5">
          <p className="text-xs text-muted-foreground mb-3">Excluded from business-day calculations. Factory may run a 6-day week — adjust here.</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button key={d} onClick={() => toggleDay(d)} data-testid={`weekend-${d}`}
                className={`px-3.5 py-2 rounded-md border text-sm transition-all ${
                  weekend.includes(d) ? "border-gold/50 bg-gold/15 text-gold" : "border-border text-muted-foreground hover:bg-muted"
                } ${!canEdit ? "cursor-default" : ""}`}>
                {d}
              </button>
            ))}
          </div>
        </Card>
      </section>

      <section>
        <SectionTitle icon={SettingsIcon}>Stage lead times (working days)</SectionTitle>
        <Card className="p-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.pipeline.map((stage) => (
              <label key={stage} className="block">
                <span className="text-xs text-muted-foreground mb-1.5 block">{data.stage_labels[stage]}</span>
                <input
                  type="number" min="0" value={lead[stage] ?? 0} disabled={!canEdit}
                  data-testid={`lead-${stage}`}
                  onChange={(e) => setLead((l) => ({ ...l, [stage]: Number(e.target.value) }))}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm font-mono-num outline-none focus:border-gold/50 disabled:opacity-70"
                />
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Changing a completion date re-plans a project's stage due dates from these lead times, walking the pipeline in reverse.
          </p>
        </Card>
      </section>

      <section>
        <SectionTitle icon={Mail} action={canEdit && (
          <button onClick={runScan} disabled={scanning || !data.email_provider_configured} data-testid="run-email-scan-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gold/30 text-gold text-xs hover:bg-gold/10 disabled:opacity-50">
            <Send size={13} /> {scanning ? "Sending…" : "Run reminder scan now"}
          </button>
        )}>Email notifications</SectionTitle>
        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Provider status:</span>
            {data.email_provider_configured
              ? <Pill tone="success" data-testid="email-status">Resend configured — emails active</Pill>
              : <Pill tone="warning" data-testid="email-status">Resend key not set — emails paused</Pill>}
          </div>
          <p className="text-xs text-muted-foreground">
            Task emails go to the owning department head: <b>assignment</b> on activation, a <b>pre-due</b> heads-up 2 business days out, and <b>overdue</b> follow-ups (at most once every 2 days). Weekly executive digest (CEO, Admin &amp; PMO) is separate.
          </p>

          <label className="block max-w-md">
            <span className="text-xs text-muted-foreground mb-1.5 block">Sender address ("from")</span>
            <input type="email" value={sender} disabled={!canEdit} data-testid="email-sender"
              onChange={(e) => setSender(e.target.value)} placeholder="no-reply@polarisae.com"
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50 disabled:opacity-70" />
            <span className="text-[11px] text-muted-foreground mt-1 block">Must be a verified domain/sender in Resend.</span>
          </label>

          <div>
            <span className="text-xs text-muted-foreground mb-2 block">Department recipients (leave blank to use each head's login email)</span>
            <div className="grid sm:grid-cols-2 gap-3">
              {(data.departments || Object.keys(DEPT_LABEL)).map((dept) => (
                <label key={dept} className="block">
                  <span className="text-xs text-foreground/80 mb-1 block">{DEPT_LABEL[dept] || dept}</span>
                  <input type="email" value={recipients[dept] || ""} disabled={!canEdit} data-testid={`recipient-${dept}`}
                    onChange={(e) => setRecipients((r) => ({ ...r, [dept]: e.target.value }))}
                    placeholder={`${dept}@polarisae.com`}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50 disabled:opacity-70" />
                </label>
              ))}
            </div>
          </div>
        </Card>
      </section>

      {canEdit && (
        <section>
          <SectionTitle icon={Users}>User accounts</SectionTitle>
          <UserAccounts users={users} onSaved={load} setMsg={setMsg} setErr={setErr} />
        </section>
      )}
    </div>
  );
}

const ROLE_ORDER = ["ceo", "admin", "pmo", "commercial", "design", "material", "procurement", "production", "projects"];

function UserAccounts({ users, onSaved, setMsg, setErr }) {
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const set = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));
  const ordered = [...users].sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a.role), ib = ROLE_ORDER.indexOf(b.role);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  const saveUser = async (u) => {
    const d = drafts[u.id] || {};
    const payload = {};
    if (d.email !== undefined && d.email !== u.email) payload.email = d.email;
    if (d.name !== undefined && d.name !== u.name) payload.name = d.name;
    if (d.password) payload.password = d.password;
    if (Object.keys(payload).length === 0) return;
    if (payload.password && payload.password.length < 8) {
      setErr("Password must be at least 8 characters."); return;
    }
    setSavingId(u.id); setErr(""); setMsg("");
    try {
      await api.put(`/users/${u.id}`, payload);
      const bits = [];
      if (payload.name || payload.email) bits.push("profile");
      if (payload.password) bits.push("password");
      setMsg(`Updated ${d.name || u.name} (${bits.join(" + ")}). Logged to Activity.`);
      setDrafts((x) => ({ ...x, [u.id]: undefined }));
      await onSaved();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingId(null); }
  };

  return (
    <Card className="p-5">
      <p className="text-xs text-muted-foreground mb-4">Set display names, login emails, and reset passwords. Roles are fixed. Passwords are write-only (never shown) — min 8 characters. Every change is recorded in the Activity feed (password values are masked).</p>
      <div className="space-y-3">
        {ordered.map((u) => {
          const d = drafts[u.id] || {};
          const dirty = (d.email !== undefined && d.email !== u.email) || (d.name !== undefined && d.name !== u.name) || !!d.password;
          return (
            <div key={u.id} className="grid sm:grid-cols-[96px_1fr_1fr_1fr_auto] gap-3 items-end border-b border-border/50 pb-3 last:border-0" data-testid={`user-row-${u.role}`}>
              <div><Pill tone="muted">{ROLE_META[u.role]?.label || u.role}</Pill></div>
              <label className="block">
                <span className="text-[11px] text-muted-foreground mb-1 block">Name</span>
                <input value={d.name ?? u.name} data-testid={`user-name-${u.role}`}
                  onChange={(e) => set(u.id, "name", e.target.value)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50" />
              </label>
              <label className="block">
                <span className="text-[11px] text-muted-foreground mb-1 block">Email</span>
                <input type="email" value={d.email ?? u.email} data-testid={`user-email-${u.role}`}
                  onChange={(e) => set(u.id, "email", e.target.value)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50" />
              </label>
              <label className="block">
                <span className="text-[11px] text-muted-foreground mb-1 block">New password</span>
                <input type="password" value={d.password || ""} autoComplete="new-password"
                  data-testid={`user-password-${u.role}`} placeholder="Leave blank to keep"
                  onChange={(e) => set(u.id, "password", e.target.value)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50" />
              </label>
              <button onClick={() => saveUser(u)} disabled={!dirty || savingId === u.id} data-testid={`user-save-${u.role}`}
                className="px-3 py-2 rounded-md text-sm border border-gold/30 text-gold hover:bg-gold/10 disabled:opacity-40">
                {savingId === u.id ? "Saving…" : "Save"}
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
